// client/src/utils/supplierOptionIdentity.ts
//
// THE IDENTITY OF ONE SUPPLIER OPTION: vendor + description, one spelling.
//
// `supplierOptions.service.js` dedupes its four-source scan on this key and
// resolves a pick by it (`optionKey`, BUSINESS_RULES#supplier-option-price-per-line-unit)
// — "the row a reviewer clicked is the row the server resolves." The quotation
// page's merged lookup panel needs the SAME key client-side (owner audit,
// 2026-09-25): `itemLookupRows.isPricedRow` used to highlight a supplier row by
// its DESCRIPTION alone, which lit up every vendor's identically-worded row
// ("EGGS WHITE 30S" from three different suppliers, all at once). One spelling
// of the identity, both runtimes, so a row the server calls "the option this
// pick named" and a row the panel highlights as that pick cannot disagree.
//
// MIRRORED FILE: src/utils/supplierOptionIdentity.js is a line-for-line twin.
// CRA's ModuleScopePlugin forbids importing from src/, so the copy is the
// only option. Change one, change both — mirroredUtilParity.test.js fails if
// you do not.

export function supplierOptionIdentityKey(option: {
  vendorName?: string | null;
  description?: string | null;
}): string {
  // N9 (owner re-audit, 2026-09-25): TRIMMED here, the one owner, so both
  // sides of an `isPricedRow` comparison are normalised identically. This
  // used to lowercase but never trim, and `itemLookupRows.isPricedRow`
  // compared a row's OWN `vendorName`/`description` (raw, as the server sent
  // them) against `pricedFromIdentity`'s already-`.trim()`ed reference
  // facts — a row carrying incidental whitespace matched the server's own
  // `optionKey` resolution but not this client-side highlight, so a pick
  // landed with no row lit up. Trimming belongs in the identity function
  // itself, not in each caller, or the next caller re-introduces the gap.
  const vendorName = option && option.vendorName != null ? String(option.vendorName).trim() : '';
  const description = option && option.description != null ? String(option.description).trim() : '';
  return `${vendorName}|${description}`.toLowerCase();
}
